import React, { type SyntheticEvent } from 'react';
import { styled } from '@compiled/react';
import type { UIAnalyticsEvent } from '@atlaskit/analytics-next';
import { xcss, Pressable, Text } from '@atlaskit/primitives';
import OldButton from '@atlaskit/button';
import { token } from '@atlaskit/tokens';
import FeedbackIcon from '@atlaskit/icon/core/feedback';
import { useActionsCommandPalette } from '@atlassian/jira-command-palette/src/controllers/command-palette/index.tsx';
import { useIntl } from '@atlassian/jira-intl';
import { fireUIAnalytics } from '@atlassian/jira-product-analytics-bridge';
import { fg } from '@atlassian/jira-feature-gating';
import { useCommandPaletteFeedbackCollector } from '../../controllers/use-feedback-collector/index.tsx';
import { messages } from '../../controllers/use-feedback-collector/messages.tsx';

export const FooterFeedbackContent = (): React.JSX.Element => {
	const [openFeedbackCollectorDialog] = useCommandPaletteFeedbackCollector();

	const { close } = useActionsCommandPalette();

	const { formatMessage } = useIntl();

	return fg('deliveroo_a11y_enghealth_fixes_oct_25') ? (
		<Pressable
			xcss={feedbackFormStyles}
			testId="command-palette-integration.ui.footer-feedback-content.feedback-button"
			onClick={(event: SyntheticEvent<unknown>, analyticsEvent: UIAnalyticsEvent) => {
				close();
				fireUIAnalytics(analyticsEvent, 'openCommandPaletteFeedbackForm');
				openFeedbackCollectorDialog();
			}}
		>
			<FeedbackIcon label="" />
			<Text color="color.text.subtle">{formatMessage(messages.giveFeedbackText)}</Text>
		</Pressable>
	) : (
		<FeedbackContentWrapper>
			<TextWrapper> {formatMessage(messages.feedbackContentText)} </TextWrapper>
			<FeedbackButton
				appearance="subtle-link"
				testId="command-palette-integration.ui.footer-feedback-content.feedback-button"
				onClick={(event: SyntheticEvent<unknown>, analyticsEvent: UIAnalyticsEvent) => {
					close();
					fireUIAnalytics(analyticsEvent, 'openCommandPaletteFeedbackForm');
					openFeedbackCollectorDialog();
				}}
			>
				{formatMessage(messages.feedbackButtonText)}
			</FeedbackButton>
		</FeedbackContentWrapper>
	);
};

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const FeedbackContentWrapper = styled.div({
	display: 'flex',
	flexDirection: 'row',
	alignItems: 'center',

	font: token('font.body.UNSAFE_small'),
	paddingTop: 0,
	paddingRight: token('space.100'),
	paddingBottom: 0,
	paddingLeft: token('space.100'),

	color: `${token('color.text.subtle')}`,
	justifyContent: 'flex-start',
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors -- Ignored via go/DSP-18766
	'button span': {
		color: `${token('color.text.subtle')}`,
	},
});

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const TextWrapper = styled.div({
	font: token('font.body.UNSAFE_small'),

	color: token('color.text.subtle'),
	alignItems: 'center',
});

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const FeedbackButton = styled(OldButton)({
	font: token('font.body.UNSAFE_small'),
	alignItems: 'center',
	fontWeight: token('font.weight.medium'),
	marginLeft: token('space.025'),
	paddingTop: 0,
	paddingRight: token('space.025'),
	paddingBottom: 0,
	paddingLeft: token('space.025'),
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-important-styles -- Ignored via go/DSP-18766
	color: `${token('color.text.subtle')} !important`,
	height: 'auto',
});

const feedbackFormStyles = xcss({
	color: 'color.text.subtle',
	display: 'flex',
	alignItems: 'center',
	paddingLeft: 'space.100',
	gap: 'space.075',
	background: 'transparent',
	border: 'none',
	cursor: 'pointer',
	':hover': {
		textDecoration: 'underline',
	},
	':focus': { textDecoration: 'underline' },
});
